import { supabase } from "@/integrations/supabase/client";
import { forwardLeadToCRM, type CRMLead } from "@/lib/crmForward";
import { getLeadContext, type LeadContext } from "@/lib/attribution";
import { isSpamSubmission, type Division } from "@/lib/leadGuard";

export type Qualification = {
  tipo_espacio?: string | null;
  num_ubicaciones?: string | null;
  plazo?: string | null;
  request_type?: string | null;
};

export type LeadCore = {
  nombre: string;
  empresa: string;
  email: string;
  telefono: string;
  /** Spanish country name (legacy column). */
  pais: string;
  mensaje: string;
};

export type LeadExtras = Qualification & {
  division: Division;
  form_name: string;
  cargo?: string | null;
  ciudad?: string | null;
  country_code?: string | null;
  /** Storage path of an uploaded implantation plan (private bucket). */
  attachment_path?: string | null;
};

type Args = {
  core: LeadCore;
  extras: LeadExtras;
  lang: string;
  honeypot: string;
  startedAt: number;
  /** Store in visit_bookings instead of leads (row is built by the caller). */
  visitRow?: Record<string, unknown>;
};

export type SubmitFailure = "slot_taken" | "invalid_contact" | "network";
export type SubmitResult = { ok: boolean; spam: boolean; reason?: SubmitFailure };

/** Max wait for the DB insert so the visitor never stays stuck on "Enviando…". */
const INSERT_TIMEOUT_MS = 15_000;

export const TEST_TAG = "[TEST]";

/** Test leads (company starting with "TEST") get "[TEST]" at the start of the message. */
export const tagTestMessage = (mensaje: string, isTest: boolean) =>
  isTest && !mensaje.startsWith(TEST_TAG) ? `${TEST_TAG} ${mensaje}`.trim() : mensaje;

const failureFrom = (error: { code?: string; message?: string } | null): SubmitFailure => {
  if (error?.code === "23505") return "slot_taken";
  if (error?.code === "22023") return "invalid_contact";
  return "network";
};

/**
 * Shared lead submit: spam guard -> await DB insert -> CRM + email in background.
 * Spam: nothing stored or sent, but the caller still shows the thank-you page.
 * Never throws and never waits longer than INSERT_TIMEOUT_MS.
 */
export async function submitLead({ core: rawCore, extras, lang, honeypot, startedAt, visitRow }: Args): Promise<SubmitResult> {
  const elapsed_ms = Date.now() - startedAt;
  if (isSpamSubmission({ honeypot, startedAt })) return { ok: true, spam: true };

  const ctx: LeadContext = getLeadContext(lang);
  const isTest = /^test\b/i.test(rawCore.empresa.trim()) || undefined;
  const core: LeadCore = { ...rawCore, mensaje: tagTestMessage(rawCore.mensaje, !!isTest) };

  let error: { code?: string; message?: string } | null = null;
  try {
    const insert = visitRow
      ? supabase.from("visit_bookings").insert({
          ...visitRow,
          division: extras.division,
          country_code: extras.country_code ?? null,
          ciudad: extras.ciudad ?? null,
          ...ctx,
          is_test: isTest ?? null,
        } as never)
      : supabase.from("leads").insert({
          ...core,
          ...extras,
          ...ctx,
          is_test: isTest ?? null,
        });
    const timeout = new Promise<{ error: { code: string; message: string } }>((resolve) =>
      setTimeout(() => resolve({ error: { code: "timeout", message: "timeout" } }), INSERT_TIMEOUT_MS),
    );
    ({ error } = await Promise.race([insert, timeout]));
  } catch (e) {
    error = { code: "network", message: e instanceof Error ? e.message : String(e) };
  }
  if (error) return { ok: false, spam: false, reason: failureFrom(error) };

  const meta = { ...extras, ...ctx, is_test: isTest ?? false, hp: honeypot, elapsed_ms };

  const crm: CRMLead = {
    company_name: core.empresa,
    contact_name: core.nombre,
    email: core.email,
    phone: core.telefono,
    country: core.pais,
    message: core.mensaje,
    division: extras.division,
    ...meta,
  };
  void forwardLeadToCRM(crm);
  void supabase.functions.invoke("send-lead-notification", { body: { ...core, ...meta } }).catch(() => {
    /* notificación secundaria */
  });

  return { ok: true, spam: false };
}
